/* WOKORA v6.8 — adaptive site-wide professional motion */
(() => {
  'use strict';
  if (window.__wokoraGlobalExperienceV68) return;
  window.__wokoraGlobalExperienceV68 = true;

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const EASE = 'cubic-bezier(.22,1,.36,1)';
  const processed = new WeakSet();
  const cardProcessed = new WeakSet();
  let observer = null;

  const V66_KNOWN = [
    '.w65-intro','.wk-cats__head','.section-heading','[data-product-card]','.product-card','.b2b-product-card',
    '.wk-biz__copy','.wk-biz__item','.collection-hero','.search-header','.list-collections-header',
    '.template-collection .facets','.template-search .facets','.template-product .product-gallery',
    '.template-product .product-info','.template-product .product-details','.template-cart .cart-items',
    '.template-cart .cart-summary','.wokora-faq__item','.business-v233__hero','.business-v233__card',
    '.w37-delivery-card','.w37-model-card','.w37-process-card'
  ].join(',');

  const isVisible = el => {
    if (!(el instanceof Element) || el.closest('[hidden],[aria-hidden="true"]')) return false;
    const style = window.getComputedStyle(el);
    if (style.display === 'none' || style.visibility === 'hidden') return false;
    const rect = el.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
  };

  const siblingDelay = (el, step = 38, cap = 190) => {
    const parent = el.parentElement;
    if (!parent) return 0;
    const siblings = Array.from(parent.children).filter(child => child.getClientRects().length);
    const index = Math.max(0, siblings.indexOf(el));
    return Math.min((index % 6) * step, cap);
  };

  const ensureObserver = () => {
    if (observer || reduceMotion.matches || !('IntersectionObserver' in window)) return observer;
    observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -7% 0px', threshold: 0.07 });
    return observer;
  };

  const playImmediate = (el, direction = 'up', delay = 0) => {
    if (reduceMotion.matches || typeof el.animate !== 'function') return;
    let transform = 'translate3d(0,14px,0)';
    if (direction === 'left') transform = 'translate3d(-14px,0,0)';
    if (direction === 'right') transform = 'translate3d(14px,0,0)';
    if (direction === 'scale') transform = 'translate3d(0,8px,0) scale(.989)';
    const animation = el.animate(
      [{ opacity: 0, transform }, { opacity: 1, transform: 'none' }],
      { duration: 540, delay, easing: EASE, fill: 'both' }
    );
    animation.onfinish = () => animation.cancel();
  };

  const reveal = (el, { direction = 'up', delay = 0 } = {}) => {
    if (!(el instanceof Element) || processed.has(el) || !isVisible(el)) return;
    processed.add(el);
    if (reduceMotion.matches) return;
    const rect = el.getBoundingClientRect();
    const viewport = window.innerHeight || document.documentElement.clientHeight;
    if (rect.bottom < 0) return;
    if (rect.top < viewport * .91) {
      playImmediate(el, direction, delay);
      return;
    }
    const io = ensureObserver();
    if (!io) return;
    el.classList.add('wk68-reveal');
    el.dataset.wk68Direction = direction;
    el.style.setProperty('--wk68-delay', `${delay}ms`);
    io.observe(el);
  };

  const isCardClass = el => Array.from(el.classList).some(className =>
    /(?:^|[-_])(card|tile|panel)$/.test(className) || className.endsWith('__card') || className.endsWith('__tile') || className.endsWith('__panel')
  );

  const enhanceCards = root => {
    const rootInMain = root === document || root?.id === 'MainContent' || Boolean(root instanceof Element && root.closest('#MainContent'));
    if (!rootInMain) return;
    const candidates = [];
    if (root instanceof Element && root.hasAttribute('class')) candidates.push(root);
    const selector = root === document ? '#MainContent [class]' : '[class]';
    root.querySelectorAll?.(selector).forEach(el => candidates.push(el));
    candidates.forEach(el => {
      if (!(el instanceof Element) || cardProcessed.has(el) || !isCardClass(el) || !isVisible(el)) return;
      if (el.closest('.w65-intro,.product-gallery,.product-main-image,[data-hero]')) return;
      const rect = el.getBoundingClientRect();
      const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
      if (rect.width < 90 || rect.height < 44 || rect.height > 900 || rect.width > Math.max(920, viewportWidth * .96)) return;
      cardProcessed.add(el);
      el.classList.add('wk68-auto-card');
      const image = el.querySelector('img');
      if (image && !image.closest('.product-gallery,.product-main-image,[data-hero]')) image.classList.add('wk68-media');
      if (!el.matches(V66_KNOWN) && !el.closest(V66_KNOWN)) reveal(el, { direction: 'scale', delay: siblingDelay(el) });
    });
  };

  const enhanceDetails = root => {
    const details = [];
    if (root instanceof HTMLDetailsElement) details.push(root);
    root.querySelectorAll?.('#MainContent details, [id="MainContent"] details').forEach(el => details.push(el));
    details.forEach(detail => detail.classList.add('wk68-details'));
  };

  const enhanceSections = root => {
    const sections = [];
    if (root instanceof Element && root.matches('#MainContent > .shopify-section')) sections.push(root);
    root.querySelectorAll?.('#MainContent > .shopify-section, [id="MainContent"] > .shopify-section').forEach(el => sections.push(el));
    sections.forEach(section => {
      if (!isVisible(section) || section.querySelector('.w65-intro') || section.querySelector(V66_KNOWN)) return;
      reveal(section, { direction: 'up' });
    });
  };

  const enhancePageTitle = root => {
    const main = root === document ? document.getElementById('MainContent') : root.closest?.('#MainContent') || root.querySelector?.('#MainContent');
    if (!main) return;
    const heading = main.querySelector('h1');
    if (!heading || processed.has(heading) || heading.closest('.w65-intro') || heading.matches(V66_KNOWN) || heading.closest(V66_KNOWN)) return;
    reveal(heading, { direction: 'up', delay: 35 });
  };

  const normalizePath = href => {
    try {
      const url = new URL(href, window.location.origin);
      return (url.pathname.replace(/\/+$/, '') || '/').toLowerCase();
    } catch (_) { return ''; }
  };

  const markCurrentNavigation = root => {
    const current = normalizePath(window.location.href);
    const links = root.querySelectorAll?.('.wk-simple-header__nav a[href], [data-drawer="menu"] a[href], .wk-mobile-menu a[href]') || [];
    links.forEach(link => {
      const target = normalizePath(link.href);
      const active = Boolean(target && target === current);
      link.classList.toggle('wk68-current', active);
      if (active) link.setAttribute('aria-current', 'page');
      else if (link.getAttribute('aria-current') === 'page') link.removeAttribute('aria-current');
    });
  };

  const process = root => {
    if (!(root instanceof Element) && root !== document) return;
    enhanceSections(root);
    enhanceCards(root);
    enhanceDetails(root);
    enhancePageTitle(root);
  };

  const touchesNavigation = root => root === document || (
    root instanceof Element && (
      root.matches('.wk-simple-header__nav,[data-drawer="menu"],.wk-mobile-menu,header,.shopify-section-group-header-group') ||
      Boolean(root.querySelector('.wk-simple-header__nav,[data-drawer="menu"],.wk-mobile-menu'))
    )
  );

  const showEverythingForReducedMotion = () => {
    if (!reduceMotion.matches) return;
    if (observer) {
      observer.disconnect();
      observer = null;
    }
    document.querySelectorAll('.wk68-reveal').forEach(el => {
      el.classList.add('is-visible');
      el.style.removeProperty('--wk68-delay');
    });
  };

  const eventRoot = event => event?.detail?.root instanceof Element ? event.detail.root : document;

  const setup = () => {
    document.documentElement.classList.add('wk68-enhanced');
    process(document);
    markCurrentNavigation(document);
    showEverythingForReducedMotion();

    document.addEventListener('shopify:section:load', event => {
      const target = event.target instanceof Element ? event.target : document;
      process(target);
      if (touchesNavigation(target)) markCurrentNavigation(document);
    });
    document.addEventListener('wokora:content-updated', event => process(eventRoot(event)));
    document.addEventListener('wokora:cart-drawer-refreshed', event => process(eventRoot(event)));

    if (typeof reduceMotion.addEventListener === 'function') reduceMotion.addEventListener('change', showEverythingForReducedMotion);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', setup, { once: true });
  else setup();
})();
